import { useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { SpeakingClassLevel } from "../gql/graphql.ts";
import {
  parseKnownListParam,
  parseListParam,
  setOrDeleteParam,
} from "../UI/Table/listUrlParams.ts";
import {
  PDF_PRESENCES,
  TOPIC_TABLE_SORT_FIELDS,
  TopicTableFilters,
  TopicTableSort,
  TopicTableSortField,
} from "./topicTableState.ts";

function isTopicTableSortField(
  value: string | undefined,
): value is TopicTableSortField {
  return (TOPIC_TABLE_SORT_FIELDS as readonly (string | undefined)[]).includes(
    value,
  );
}

function parseSort(value: string | null): TopicTableSort | undefined {
  const [sortBy, sortDirection] = value?.split(":") ?? [];
  const isKnownDirection = sortDirection === "asc" || sortDirection === "desc";
  return isTopicTableSortField(sortBy) && isKnownDirection
    ? { sortBy, sortDirection }
    : undefined;
}

export function useTopicTableUrlState() {
  const [searchParams, setSearchParams] = useSearchParams();

  const sort = useMemo(
    () => parseSort(searchParams.get("sort")),
    [searchParams],
  );

  const filters = useMemo<TopicTableFilters>(
    () => ({
      search: searchParams.get("q") ?? "",
      levels: parseKnownListParam(
        searchParams.get("levels"),
        Object.values(SpeakingClassLevel),
      ),
      pdfPresences: parseKnownListParam(searchParams.get("pdf"), PDF_PRESENCES),
      userGroupIds: parseListParam(searchParams.get("groups")),
    }),
    [searchParams],
  );

  const updateParams = useCallback(
    (update: (params: URLSearchParams) => void) =>
      setSearchParams(
        (current) => {
          const params = new URLSearchParams(current);
          update(params);
          return params;
        },
        { replace: true },
      ),
    [setSearchParams],
  );

  const setSort = useCallback(
    (nextSort: TopicTableSort | undefined) =>
      updateParams((params) =>
        setOrDeleteParam(
          params,
          "sort",
          nextSort && `${nextSort.sortBy}:${nextSort.sortDirection}`,
        ),
      ),
    [updateParams],
  );

  const setSearch = useCallback(
    (search: string) =>
      updateParams((params) => setOrDeleteParam(params, "q", search.trim())),
    [updateParams],
  );

  const setColumnFilters = useCallback(
    ({
      levels,
      pdfPresences,
      userGroupIds,
    }: Omit<TopicTableFilters, "search">) =>
      updateParams((params) => {
        setOrDeleteParam(params, "levels", levels.join(","));
        setOrDeleteParam(params, "pdf", pdfPresences.join(","));
        setOrDeleteParam(params, "groups", userGroupIds.join(","));
      }),
    [updateParams],
  );

  return { sort, filters, setSort, setSearch, setColumnFilters };
}
